import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, Sparkles, Clock, Calendar } from 'lucide-react';
import { playSoundIfEnabled } from '../utils/sounds';
import { aboutInfo } from '../mock/mockData';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validate()) {
      playSoundIfEnabled('success');
      toast.success('Message sent successfully! (Mock submission)');
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } else {
      playSoundIfEnabled('click');
    }
  };

  return (
    <div className="min-h-screen bg-black pt-24 pb-16">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-light-pink/10 rounded-full px-4 py-2 mb-4">
            <Mail className="w-4 h-4 text-light-pink" />
            <span className="text-light-pink text-sm font-mono uppercase tracking-wider">
              Get In Touch
            </span>
          </div>
          <h1 className="text-5xl lg:text-6xl font-display font-bold text-white mb-6">
            Contact Us
          </h1>
          <p className="text-xl text-mid-grey max-w-3xl mx-auto">
            Have questions about our workshops? Want to learn more? We'd love to hear from you!
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {/* Contact Information */}
          <div className="lg:col-span-1 space-y-6">
            {/* Contact Cards */}
            <div className="bg-gradient-to-br from-light-pink/20 to-light-pink/5 rounded-2xl p-6 border border-light-pink/20">
              <div className="w-12 h-12 bg-light-pink rounded-xl flex items-center justify-center mb-4">
                <Mail className="w-6 h-6 text-black" />
              </div>
              <h3 className="text-white font-semibold mb-2">Email Us</h3>
              <a 
                href={`mailto:${aboutInfo.contact.email}`}
                className="text-light-pink hover:text-white transition-colors break-all"
                onClick={() => playSoundIfEnabled('click')}
              >
                {aboutInfo.contact.email}
              </a>
            </div>

            <div className="bg-gradient-to-br from-mid-purple/20 to-mid-purple/5 rounded-2xl p-6 border border-mid-purple/20">
              <div className="w-12 h-12 bg-mid-purple rounded-xl flex items-center justify-center mb-4">
                <Phone className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-white font-semibold mb-2">Call Us</h3>
              <a 
                href={`tel:${aboutInfo.contact.phone}`}
                className="text-mid-purple hover:text-white transition-colors"
                onClick={() => playSoundIfEnabled('click')}
              >
                {aboutInfo.contact.phone}
              </a>
            </div>

            <div className="bg-gradient-to-br from-light-yellow/20 to-light-yellow/5 rounded-2xl p-6 border border-light-yellow/20">
              <div className="w-12 h-12 bg-light-yellow rounded-xl flex items-center justify-center mb-4">
                <MapPin className="w-6 h-6 text-black" />
              </div>
              <h3 className="text-white font-semibold mb-2">Location</h3>
              <p className="text-light-yellow">{aboutInfo.contact.location}</p>
            </div>

            {/* Office Hours */}
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
              <div className="flex items-center gap-2 mb-4">
                <Clock className="w-5 h-5 text-light-pink" />
                <h3 className="text-white font-semibold">Office Hours</h3>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-mid-grey">Monday - Friday</span>
                  <span className="text-white">9:00 AM - 5:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-mid-grey">Saturday</span>
                  <span className="text-white">10:00 AM - 2:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-mid-grey">Sunday</span>
                  <span className="text-white">Closed</span>
                </div>
              </div>
            </div>

            {/* Response Time */}
            <div className="bg-gradient-to-br from-mid-green/20 to-mid-green/5 rounded-2xl p-6 border border-mid-green/20">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="w-5 h-5 text-mid-green" />
                <h3 className="text-white font-semibold">Response Time</h3>
              </div>
              <p className="text-mid-grey text-sm">
                We typically respond within 24 hours during business days
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-8 lg:p-12 border border-white/10">
              <div className="flex items-center gap-2 mb-6">
                <Sparkles className="w-6 h-6 text-light-pink" />
                <h2 className="text-2xl font-display font-bold text-white">Send Us a Message</h2>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-white mb-2 font-semibold">Your Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-light-pink transition-colors"
                      placeholder="Enter your name"
                    />
                    {errors.name && <p className="text-red-400 text-sm mt-1">{errors.name}</p>}
                  </div>

                  <div>
                    <label className="block text-white mb-2 font-semibold">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-light-pink transition-colors"
                      placeholder="your.email@example.com"
                    />
                    {errors.email && <p className="text-red-400 text-sm mt-1">{errors.email}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-white mb-2 font-semibold">Phone Number</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-light-pink transition-colors"
                      placeholder="+44 1234 567890"
                    />
                  </div>

                  <div>
                    <label className="block text-white mb-2 font-semibold">Subject</label>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-light-pink transition-colors"
                      placeholder="What's this about?"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-white mb-2 font-semibold">Your Message *</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="6"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-light-pink transition-colors resize-none"
                    placeholder="Tell us how we can help..."
                  />
                  {errors.message && <p className="text-red-400 text-sm mt-1">{errors.message}</p>}
                </div>

                <div className="bg-light-pink/10 rounded-xl p-4 border border-light-pink/20">
                  <p className="text-light-pink text-sm">
                    <strong>Note:</strong> This is a mock contact form. In the full version, your message 
                    would be sent to our team and you'd receive a confirmation email.
                  </p>
                </div>

                <button
                  type="submit"
                  className="w-full cta-button large bg-light-pink text-black hover:bg-mid-yellow transition-all duration-300 inline-flex items-center justify-center gap-2 group"
                  onClick={() => playSoundIfEnabled('click')}
                >
                  Send Message
                  <Send className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
              </form>
            </div>

            {/* Additional Info */}
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gradient-to-br from-light-pink/10 to-mid-purple/10 rounded-2xl p-6 border border-light-pink/20">
                <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-light-pink" />
                  Workshop Enquiries
                </h3>
                <p className="text-mid-grey text-sm">
                  Questions about specific workshops, availability, or age suitability? We're happy 
                  to help you find the perfect workshop for your child.
                </p>
              </div>

              <div className="bg-gradient-to-br from-mid-purple/10 to-light-yellow/10 rounded-2xl p-6 border border-mid-purple/20">
                <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-mid-purple" />
                  General Enquiries
                </h3>
                <p className="text-mid-grey text-sm">
                  Have questions about our approach, facilities, or team? Want to discuss group 
                  bookings or special requirements? Get in touch!
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
